/**
 * Shared calendar/period utilities used by period-dropdown and schedule-shift-modal.
 */

export const CALENDAR_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export function getCurrentMonthYear(date: Date): string {
  return `${CALENDAR_MONTHS[date.getMonth()]} ${date.getFullYear()}`;
}

export function navigateMonth(date: Date, direction: 'prev' | 'next'): Date {
  const newDate = new Date(date);
  if (direction === 'next') {
    newDate.setMonth(newDate.getMonth() + 1);
  } else {
    newDate.setMonth(newDate.getMonth() - 1);
  }
  return newDate;
}

export interface CalendarDay {
  day: number;
  date: Date;
  isOtherMonth: boolean;
}

/**
* Generate a 6-week (42-day) calendar grid for the month containing the given anchor date, starting on the Sunday before or on the month's first day.
* @example
* getCalendarDays(new Date(2023, 4, 15))
* [{ day: 30, date: new Date(2023, 3, 30), isOtherMonth: true }, { day: 1, date: new Date(2023, 4, 1), isOtherMonth: false }, ...]
* @param {Date} anchorDate - Anchor date used to determine the month and year for which to generate calendar days.
* @returns {CalendarDay[]} Array of 42 CalendarDay objects, each with day (number), date (Date), and isOtherMonth (boolean).
**/
export function getCalendarDays(anchorDate: Date): CalendarDay[] {
  const year = anchorDate.getFullYear();
  const month = anchorDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - firstDay.getDay());

  const days: CalendarDay[] = [];
  const currentDate = new Date(startDate);

  for (let i = 0; i < 42; i += 1) {
    days.push({
      day: currentDate.getDate(),
      date: new Date(currentDate),
      isOtherMonth: currentDate.getMonth() !== month,
    });
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return days;
}

export function isToday(date: Date): boolean {
  const today = new Date();
  return date.toDateString() === today.toDateString();
}

export function isDayInSelectedWeek(date: Date, selectedWeekStart: Date | null): boolean {
  if (!selectedWeekStart) return false;
  const weekStart = new Date(selectedWeekStart);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  return date >= weekStart && date <= weekEnd;
}

/**
 * Get the Sunday (start of week) for a given date.
 */
export function getWeekStart(date: Date): Date {
  const weekStart = new Date(date);
  const dayOfWeek = weekStart.getDay();
  weekStart.setDate(weekStart.getDate() - dayOfWeek);
  return weekStart;
}
